// @givmo/org-console — the tolerant decode, and the display helpers the panels share.
//
// NOTHING HERE THROWS. These are authorization screens: a single odd row must degrade to a line that
// says less, never to a blank list, and never to an error boundary that hides who can act for an
// organization.
//
// ⚠ EVERY LIST READ GOES THROUGH `extractList`. The response middleware rewrites a
// `{data, meta, links}` body to the bare `data` array before the call resolves, so the bare array is
// what actually arrives — but `extractList` reads both shapes and returns `[]` for anything else, so
// a screen is correct whether or not the unwrap happened. Type the input `unknown`, never a
// generated envelope type.
//
// ⚠ EVERY BOOLEAN IS READ AS `=== true`. A string, a number or an object arriving on a boolean is a
// shape this build does not understand, and reading it as "yes" guesses in the direction that GRANTS
// access. On this surface that is the one direction a tolerant reader must never guess in.
//
// ⚠ `meta` IS UNREACHABLE, so nothing here can report a total. {@link nextOffset} is the whole paging
// rule: a page shorter than the page size is the last one, and the offset advances by rows loaded.

import { extractList } from '@givmo/api-client/list';
import { normalizeModuleActivationReason, normalizeModuleActivationState } from './module-verdict';
import { titleizeSlug } from './org-console-config';
import type {
  OrgAccessEventRow,
  OrgAccessPermission,
  OrgAccessRequestRow,
  OrgAccessRequestStatus,
  OrgAccessRequestSummary,
  OrgAccessSettings,
  OrgAdminPreferenceDefinition,
  OrgAdminPreferences,
  OrgConnectedAiRow,
  OrgConsoleMemberRow,
  OrgDeactivationResult,
  OrgInviteAccepted,
  OrgInvitePreview,
  OrgInviteCreated,
  OrgInviteRow,
  OrgMeasurementConfig,
  OrgMembershipRow,
  OrgModuleAccessRow,
  OrgModuleCatalogRow,
  OrgModuleGrantOffer,
  OrgModuleTerms,
  OrgModuleTermsState,
  OrgAddress,
  OrgRecordAddress,
  OrgSelfAccess,
  OrgSettingChoice,
  OrgSettingDefinition,
  OrgSettingRecord,
  OrgSettingRecordFact,
  OrgSettings,
  OrgSettingState,
} from './wire-types';

// ── Primitive coercions ──────────────────────────────────────────────────────

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** A trimmed non-empty string, or `null`. */
function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

/** `true` only for the literal boolean. See the header. */
function bool(value: unknown): boolean {
  return value === true;
}

/** The string members of an unknown array, dropping everything else. */
function strings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

// ── Rows ─────────────────────────────────────────────────────────────────────

/** The four request statuses, read as literals. Anything else is unreadable. */
function requestStatus(value: unknown): OrgAccessRequestStatus | null {
  return value === 'PENDING' || value === 'APPROVED' || value === 'DENIED' || value === 'VOID'
    ? value
    : null;
}

/** The standing read's per-module `access_request` summary, or `null` when unreadable. */
function decodeAccessRequestSummary(raw: unknown): OrgAccessRequestSummary | null {
  if (!isRecord(raw)) return null;
  const id = str(raw.id);
  const status = requestStatus(raw.status);
  if (id === null || status === null) return null;
  return {
    id,
    status,
    requested_role: str(raw.requested_role) ?? '',
    created_at: str(raw.created_at),
    resolved_at: str(raw.resolved_at),
    resolution_note: str(raw.resolution_note),
  };
}

/** A non-negative integer, or `null`. A count is a claim, so nothing else is coerced into one. */
function count(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : null;
}

function decodeModuleAccess(raw: unknown): OrgModuleAccessRow | null {
  if (!isRecord(raw)) return null;
  const module = str(raw.module);
  if (module === null) return null;
  const activationState = normalizeModuleActivationState(raw.activation_state);
  const accessRequest = decodeAccessRequestSummary(raw.access_request);
  const pending = count(raw.pending_access_requests);
  return {
    module,
    has_access: bool(raw.has_access),
    // An unrecognized tier resolves to the least-privilege reading on the backend's read side too,
    // so an empty string here is honest rather than a guess at Owner.
    granted_role: str(raw.granted_role) ?? '',
    enabled_for_org: bool(raw.enabled_for_org),
    ...(activationState === null ? {} : { activation_state: activationState }),
    // The one boolean read as `=== false`: an unswitchable module is the newer, rarer fact, and
    // guessing it would hide a real switch from the person who owns it.
    ...(raw.switchable === false ? { switchable: false } : {}),
    custom_permissions: bool(raw.custom_permissions),
    effective_permissions: strings(raw.effective_permissions),
    ...(accessRequest === null ? {} : { access_request: accessRequest }),
    ...(pending === null ? {} : { pending_access_requests: pending }),
  };
}

function decodeModules(raw: unknown): OrgModuleAccessRow[] {
  return Array.isArray(raw)
    ? raw.map(decodeModuleAccess).filter((row): row is OrgModuleAccessRow => row !== null)
    : [];
}

/** One roster row, or `null` when there is no id to key it by. */
export function decodeOrgMember(raw: unknown): OrgConsoleMemberRow | null {
  if (!isRecord(raw)) return null;
  const userId = str(raw.user_id);
  if (userId === null) return null;
  return {
    user_id: userId,
    first_name: str(raw.first_name),
    last_name: str(raw.last_name),
    email: str(raw.email),
    base_role: str(raw.base_role),
    is_global_admin: bool(raw.is_global_admin),
    modules: decodeModules(raw.modules),
    created_at: str(raw.created_at),
    // Present only as `true` on a marked row; every row an older backend serves reads not marked.
    external: bool(raw.external),
  };
}

/** A page of roster rows, dropping only the ones that cannot be keyed. */
export function decodeOrgMembers(body: unknown): OrgConsoleMemberRow[] {
  return extractList<unknown>(body)
    .map(decodeOrgMember)
    .filter((row): row is OrgConsoleMemberRow => row !== null);
}

function decodePermission(raw: unknown): OrgAccessPermission | null {
  if (!isRecord(raw)) return null;
  const key = str(raw.key);
  if (key === null) return null;
  return { key, level: str(raw.level) ?? '' };
}

/**
 * A page of catalog rows.
 *
 * ⚠ THERE IS NO NAMED WRAPPER TO READ. The org catalog is a paginated LIST; a wrapper-shaped read
 * over a list body finds nothing, coerces to `[]`, and renders an editor with no module rows at all
 * — which looks exactly like "this organization has no apps".
 */
export function decodeOrgCatalog(body: unknown): OrgModuleCatalogRow[] {
  return extractList<unknown>(body)
    .map((raw): OrgModuleCatalogRow | null => {
      if (!isRecord(raw)) return null;
      const module = str(raw.module);
      if (module === null) return null;
      return {
        module,
        default_allow: bool(raw.default_allow),
        permissions: Array.isArray(raw.permissions)
          ? raw.permissions
              .map(decodePermission)
              .filter((row): row is OrgAccessPermission => row !== null)
          : [],
      };
    })
    .filter((row): row is OrgModuleCatalogRow => row !== null);
}

/**
 * The org-wide module switches.
 *
 * A module whose legacy value is absent or unreadable resolves to ON, preserving the tolerant
 * behavior required against a backend that predates the additive hook state. Current backends
 * already resolve activation-mode defaults before serialization (ordinary rowless modules ON,
 * provisioning hooks OFF) and emit a recognized hook state; that state wins. A missing or malformed
 * new state deliberately leaves the legacy deploy-skew behavior untouched.
 */
/** The terms posture, read as a literal. Absent or unrecognized reads as `none` — every module today. */
function termsState(value: unknown): OrgModuleTermsState {
  return value === 'required' || value === 'accepted' ? value : 'none';
}

export function decodeOrgAccessSettings(body: unknown): OrgAccessSettings | null {
  if (!isRecord(body)) return null;
  const raw = body.modules;
  if (!isRecord(raw)) {
    return { modules: {}, activations: {}, switchable: {}, terms: {} };
  }
  const modules: Record<string, boolean> = {};
  const activations: OrgAccessSettings['activations'] = {};
  const switchable: Record<string, boolean> = {};
  const terms: Record<string, OrgModuleTermsState> = {};
  for (const [slug, value] of Object.entries(raw)) {
    const setting = isRecord(value) ? value : null;
    const legacyEnabled = setting === null || setting.enabled !== false;
    const activationState = normalizeModuleActivationState(setting?.activation_state);
    modules[slug] = activationState === null ? legacyEnabled : activationState === 'on';
    if (activationState !== null) {
      activations[slug] = {
        activation_state: activationState,
        // The backend permits a reason only on OFF. Enforce that presentation boundary again here;
        // a reason beside an ON/SETTING-UP switch would be contradictory even if a malformed peer
        // supplied one.
        activation_reason:
          activationState === 'off'
            ? normalizeModuleActivationReason(setting?.activation_reason)
            : null,
      };
    }
    // Absent ⇒ switchable: the always-on mode is the additive, rarer fact (see wire-types.ts).
    switchable[slug] = setting?.switchable !== false;
    terms[slug] = termsState(setting?.terms_state);
  }
  return { modules, activations, switchable, terms };
}

/**
 * The organization's measurement configuration.
 *
 * ⚠ EVERY FIELD IS NULLABLE AND A NULL IS A REAL ANSWER, not a decode failure: an organization that
 * has never configured anything reads as this object with all five null, and the store's own contract
 * is that an all-null row and no row at all are one state. So an unreadable body yields `null` (there
 * is no answer yet), while a readable one with nothing in it yields an object of nulls (the answer is
 * "nothing is configured"). A form must be able to tell those apart: the first is still loading or
 * broken, the second is an empty form someone can fill in.
 *
 * `str` trims and rejects blanks, so a whitespace-only id arriving from anywhere reads as absent
 * rather than as an id nobody can see.
 */
export function decodeOrgMeasurementConfig(body: unknown): OrgMeasurementConfig | null {
  if (!isRecord(body)) return null;
  return {
    ga4_measurement_id: str(body.ga4_measurement_id),
    meta_pixel_id: str(body.meta_pixel_id),
    gtm_container_id: str(body.gtm_container_id),
    updated_at: str(body.updated_at),
    updated_by_user_id: str(body.updated_by_user_id),
  };
}

/** A page of access-event rows, dropping only the ones that cannot be keyed. */
export function decodeOrgAccessEvents(body: unknown): OrgAccessEventRow[] {
  return extractList<unknown>(body)
    .map((raw): OrgAccessEventRow | null => {
      if (!isRecord(raw)) return null;
      const id = str(raw.id);
      if (id === null) return null;
      return {
        id,
        org_ref: str(raw.org_ref) ?? '',
        actor_user_id: str(raw.actor_user_id),
        subject_user_id: str(raw.subject_user_id),
        event_type: str(raw.event_type) ?? '',
        payload: isRecord(raw.payload) ? raw.payload : null,
        occurred_at: str(raw.occurred_at),
      };
    })
    .filter((row): row is OrgAccessEventRow => row !== null);
}

/** The caller's own standing, or `null` when the answer is not an object at all. */
export function decodeOrgSelfAccess(body: unknown): OrgSelfAccess | null {
  if (!isRecord(body)) return null;
  const pending = count(body.pending_access_requests);
  const landing = str(body.default_landing_module);
  return {
    org_ref: str(body.org_ref) ?? '',
    is_global_admin: bool(body.is_global_admin),
    permissions: strings(body.permissions),
    modules: decodeModules(body.modules),
    // ⚠ `null`, NOT `[]`, when the wire carried no list: an absent field is a backend predating it,
    // and reading that as "holds no capability" would hide the Admin Console from every Global
    // Admin for the length of a rollout. `standing-read.ts` treats `null` as NOT REPORTED.
    capabilities: Array.isArray(body.capabilities) ? strings(body.capabilities) : null,
    ...(pending === null ? {} : { pending_access_requests: pending }),
    ...(landing === null ? {} : { default_landing_module: landing }),
    // OM-33: carried through only when the wire carries a list; `standingReadFrom` normalizes it.
    ...(Array.isArray(body.coming_soon_modules)
      ? { coming_soon_modules: strings(body.coming_soon_modules) }
      : {}),
    // The organization's time zone in force; carried only when the wire carries one.
    ...(str(body.time_zone) === null ? {} : { time_zone: str(body.time_zone) }),
  };
}

// ── Access requests (shell contract v3) ──────────────────────────────────────

/** One access request row, or `null` when there is no id to key it by. */
export function decodeOrgAccessRequest(raw: unknown): OrgAccessRequestRow | null {
  if (!isRecord(raw)) return null;
  const id = str(raw.id);
  const module = str(raw.module);
  const status = requestStatus(raw.status);
  // A row with no module or no readable status cannot be acted on — no approve, no deny, no
  // statement — so it is dropped rather than rendered as a control with nothing behind it.
  if (id === null || module === null || status === null) return null;
  // The requester may arrive nested (`requester: {user_id, name, email}`) or flat; both are read.
  const requester = isRecord(raw.requester) ? raw.requester : null;
  const first = str(requester?.first_name ?? raw.requester_first_name);
  const last = str(requester?.last_name ?? raw.requester_last_name);
  const joined = [first, last].filter(Boolean).join(' ').trim();
  return {
    id,
    org_ref: str(raw.org_ref) ?? '',
    requester_user_id: str(requester?.user_id ?? raw.requester_user_id) ?? '',
    requester_name:
      str(requester?.name ?? raw.requester_name) ?? (joined.length > 0 ? joined : null),
    requester_email: str(requester?.email ?? raw.requester_email),
    module,
    requested_role: str(raw.requested_role) ?? '',
    explanation: str(raw.explanation),
    status,
    resolved_by_user_id: str(raw.resolved_by_user_id),
    resolution_note: str(raw.resolution_note),
    created_at: str(raw.created_at),
    resolved_at: str(raw.resolved_at),
  };
}

/** A page of access requests, dropping only the rows that cannot be acted on. */
export function decodeOrgAccessRequests(body: unknown): OrgAccessRequestRow[] {
  return extractList<unknown>(body)
    .map(decodeOrgAccessRequest)
    .filter((row): row is OrgAccessRequestRow => row !== null);
}

/** The requester's display name — the projected name, then the email, then a neutral word. */
export function accessRequesterName(row: OrgAccessRequestRow): string {
  if (row.requester_name !== null && row.requester_name.length > 0) return row.requester_name;
  if (row.requester_email !== null && row.requester_email.length > 0) return row.requester_email;
  return 'A teammate';
}

// ── The admin's own preferences (shell contract v3) ──────────────────────────

function decodePreferenceDefinition(raw: unknown): OrgAdminPreferenceDefinition | null {
  if (!isRecord(raw)) return null;
  const key = str(raw.key);
  const kind = raw.kind === 'boolean' || raw.kind === 'choice' ? raw.kind : null;
  // A definition with no key cannot be written back; one of an unknown KIND cannot be rendered as
  // a control the page knows. Both are dropped rather than drawn as something inert.
  if (key === null || kind === null) return null;
  return {
    key,
    module: str(raw.module) ?? '',
    kind,
    choices: strings(raw.choices),
    default: 'default' in raw ? raw.default : null,
    // A label the server forgot renders as the key rather than as an unlabelled control.
    label: str(raw.label) ?? key,
    description: str(raw.description),
  };
}

/** The preferences page's `{definitions, values}` read, or `null` when the body is not an object. */
export function decodeOrgAdminPreferences(body: unknown): OrgAdminPreferences | null {
  if (!isRecord(body)) return null;
  const definitions = Array.isArray(body.definitions)
    ? body.definitions
        .map(decodePreferenceDefinition)
        .filter((row): row is OrgAdminPreferenceDefinition => row !== null)
    : [];
  return { definitions, values: isRecord(body.values) ? { ...body.values } : {} };
}

// ── The organization's settings — seam A (Org Machinery OM-17) ───────────────

/**
 * A `choice` definition's choices, in either shape the wire has carried: `{value, label}` as the
 * server serves it (design 4A), or a bare value string (design 4's shape), whose word is the value
 * titleized. A choice with no value cannot be written back, so it is dropped; the VALUE is kept
 * byte for byte, since it is what the write sends.
 */
function settingChoices(value: unknown): OrgSettingChoice[] {
  if (!Array.isArray(value)) return [];
  const choices: OrgSettingChoice[] = [];
  for (const item of value) {
    if (typeof item === 'string') {
      if (item.length > 0) choices.push({ value: item, label: titleizeSlug(item) });
    } else if (isRecord(item) && typeof item.value === 'string' && item.value.length > 0) {
      choices.push({ value: item.value, label: str(item.label) ?? titleizeSlug(item.value) });
    }
  }
  return choices;
}

function decodeSettingDefinition(raw: unknown): OrgSettingDefinition | null {
  if (!isRecord(raw)) return null;
  const key = str(raw.key);
  const kind =
    raw.kind === 'boolean' || raw.kind === 'choice' || raw.kind === 'text' || raw.kind === 'address'
      ? raw.kind
      : null;
  // A definition with no key cannot be written back; one of an unknown KIND cannot be rendered as
  // a control the page knows. Both are dropped rather than drawn as something inert.
  if (key === null || kind === null) return null;
  const order = typeof raw.order === 'number' && Number.isInteger(raw.order) ? raw.order : 0;
  return {
    key,
    module: str(raw.module) ?? '',
    kind,
    choices: settingChoices(raw.choices),
    default: 'default' in raw ? raw.default : null,
    // A label the server forgot renders as the key rather than as an unlabelled control.
    label: str(raw.label) ?? key,
    description: str(raw.description),
    placeholder: str(raw.placeholder),
    // A missing gate admits any admin — the same reading the sidebar gives an empty `requires`.
    requires: str(raw.requires) ?? '',
    order,
    // ADDITIVE (Design 4C): a body without `fact` reads as a setting, exactly as before; a body
    // without `can_change` leaves the verdict to `requires`.
    fact: bool(raw.fact),
    can_change: typeof raw.can_change === 'boolean' ? raw.can_change : null,
  };
}

/**
 * A kept address as the store holds it, or `null` when the value is not one: line 1, the city, the
 * state and the ZIP code must read; the optional parts degrade to `null`, the country to `US`.
 */
export function decodeOrgAddress(value: unknown): OrgAddress | null {
  if (!isRecord(value)) return null;
  const line1 = str(value.line1);
  const city = str(value.city);
  const state = str(value.state);
  const postalCode = str(value.postal_code);
  if (line1 === null || city === null || state === null || postalCode === null) return null;
  return {
    line1,
    line2: str(value.line2),
    city,
    state,
    postal_code: postalCode,
    county: str(value.county),
    country: str(value.country) ?? 'US',
  };
}

function decodeRecordAddress(value: unknown): OrgRecordAddress | null {
  if (!isRecord(value)) return null;
  const address: OrgRecordAddress = {
    in_care_of: str(value.in_care_of),
    line1: str(value.line1),
    line2: str(value.line2),
    city: str(value.city),
    state: str(value.state),
    postal_code: str(value.postal_code),
  };
  return Object.values(address).every((part) => part === null) ? null : address;
}

function decodeRecordFact(raw: unknown): OrgSettingRecordFact | null {
  if (!isRecord(raw)) return null;
  const key = str(raw.key);
  const label = str(raw.label);
  const kind = raw.kind === 'text' || raw.kind === 'address' ? raw.kind : null;
  // A fact with no key or label cannot be named on the card; one of an unknown kind cannot be drawn.
  if (key === null || label === null || kind === null) return null;
  return {
    key,
    label,
    kind,
    value: kind === 'address' ? decodeRecordAddress(raw.value) : str(raw.value),
    description: str(raw.description),
  };
}

/**
 * One outside record (Design 4C §2), or `null` when it cannot be drawn honestly: a record with no
 * source, label or detail has no card to be.
 */
function decodeSettingRecord(raw: unknown): OrgSettingRecord | null {
  if (!isRecord(raw)) return null;
  const source = str(raw.source);
  const label = str(raw.label);
  const detail = str(raw.detail);
  if (source === null || label === null || detail === null) return null;
  return {
    source,
    label,
    detail,
    as_of: str(raw.as_of),
    change_hint: str(raw.change_hint) ?? '',
    listed: bool(raw.listed),
    facts: Array.isArray(raw.facts)
      ? raw.facts
          .map(decodeRecordFact)
          .filter((fact): fact is OrgSettingRecordFact => fact !== null)
      : [],
    // ADDITIVE (D-CPO-69): a body that serves no lines draws none — the card never words them itself.
    not_listed_lines: Array.isArray(raw.not_listed_lines)
      ? raw.not_listed_lines.flatMap((line) => {
          const text = str(line);
          return text === null ? [] : [text];
        })
      : [],
  };
}

/**
 * One key's stored state. An entry without a readable revision is dropped, so the key reads as
 * revision 0 — never set; the attribution fields degrade to `null` one at a time.
 */
function decodeSettingState(raw: unknown): OrgSettingState | null {
  if (!isRecord(raw)) return null;
  const revision = count(raw.revision);
  if (revision === null) return null;
  return {
    revision,
    updated_by_user_id: str(raw.updated_by_user_id),
    updated_at: str(raw.updated_at),
    channel: str(raw.channel),
  };
}

/**
 * The org settings read's `{definitions, values, state, records, entered_address_on_file}`, or `null`
 * when the body is not an object.
 */
export function decodeOrgSettings(body: unknown): OrgSettings | null {
  if (!isRecord(body)) return null;
  const definitions = Array.isArray(body.definitions)
    ? body.definitions
        .map(decodeSettingDefinition)
        .filter((row): row is OrgSettingDefinition => row !== null)
    : [];
  // ADDITIVE (design 4A): a body without `state` — or with an unreadable entry — reads as never set.
  const state: Record<string, OrgSettingState> = {};
  if (isRecord(body.state)) {
    for (const [key, raw] of Object.entries(body.state)) {
      const entry = decodeSettingState(raw);
      if (entry !== null) state[key] = entry;
    }
  }
  // ADDITIVE (Design 4C): a body without `records` — an older backend, or a class with no outside
  // record — has none.
  const records = Array.isArray(body.records)
    ? body.records
        .map(decodeSettingRecord)
        .filter((record): record is OrgSettingRecord => record !== null)
    : [];
  // ADDITIVE (OM-92): only a served boolean says anything; anything else — absent from an older
  // backend, `null` for someone who may not deactivate — is not told.
  const enteredAddressOnFile =
    typeof body.entered_address_on_file === 'boolean' ? body.entered_address_on_file : null;
  return {
    definitions,
    values: isRecord(body.values) ? { ...body.values } : {},
    state,
    records,
    entered_address_on_file: enteredAddressOnFile,
  };
}

/**
 * A record's own date ("Oct 14, 2026") — an ISO DAY, read as that calendar day wherever the viewer
 * is (a bare date parsed as an instant is midnight UTC, the day before in the Americas). '' when
 * unreadable.
 */
export function formatOrgRecordDay(day: string | null): string {
  if (day === null) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (match === null) return '';
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// ── Module terms (shell contract v3) ─────────────────────────────────────────

/** A version number as the wire spells it (an integer), or a string, else `null`. */
function versionString(value: unknown): string | null {
  if (typeof value === 'number' && Number.isInteger(value)) return String(value);
  return str(value);
}

/**
 * The instrument a module binds, or `null` when the body carries no hash to accept against.
 *
 * ⚠ THE HASH IS THE ONE REQUIRED FIELD, AND THE WIRE SPELLS IT `current_content_hash` (PR-BE3,
 * backend `5fec8d4e1` — `OrgModuleTermsStateOut`; a plain `content_hash` is tolerated for a body
 * that spells it that way). Acceptance is "this organization accepted THIS version", and the
 * accept operation refuses a stale hash — so an instrument the client cannot name by hash is one
 * it cannot honestly offer to accept.
 */
export function decodeOrgModuleTerms(body: unknown): OrgModuleTerms | null {
  if (!isRecord(body)) return null;
  const contentHash = str(body.current_content_hash) ?? str(body.content_hash);
  if (contentHash === null) return null;
  return {
    module: str(body.module) ?? '',
    accepted: bool(body.accepted),
    version: versionString(body.current_version_number ?? body.version),
    effective_at: str(body.current_effective_at),
    content: str(body.content),
    content_hash: contentHash,
    accepted_version: versionString(body.accepted_version_number),
    accepted_by_user_id: str(body.accepted_by_user_id),
    accepted_at: str(body.accepted_at),
  };
}

/**
 * The caller's memberships, across every class.
 *
 * ⚠ A ROW WITH NO `entity_class` IS DROPPED, not defaulted. That field is the whole point of this
 * read — a host uses it to decide whether it can render an organization at all — so a row missing it
 * carries no usable answer, and inventing one would let a host act on a class the wire never named.
 * `org_ref` is required for the same reason: a membership with nothing to address is not a
 * membership.
 */
export function decodeOrgMemberships(body: unknown): OrgMembershipRow[] {
  return extractList<unknown>(body)
    .map((raw): OrgMembershipRow | null => {
      if (!isRecord(raw)) return null;
      const orgRef = str(raw.org_ref);
      const entityClass = str(raw.entity_class);
      if (orgRef === null || entityClass === null) return null;
      return {
        org_ref: orgRef,
        entity_class: entityClass,
        name: str(raw.name),
        role: str(raw.role),
        image_url: str(raw.image_url),
      };
    })
    .filter((row): row is OrgMembershipRow => row !== null);
}

// ── Invites (OM-10) ──────────────────────────────────────────────────────────

/** The `{module, granted_role}` pairs of an offer, dropping any that names no module. */
function decodeGrantOffers(raw: unknown): OrgModuleGrantOffer[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry): OrgModuleGrantOffer | null => {
      if (!isRecord(entry)) return null;
      const module = str(entry.module);
      if (module === null) return null;
      // An unreadable tier resolves to the least-privilege reading, matching the backend's own
      // read-side rule and `moduleTier`'s: guessing Owner here would state a wider offer than
      // whatever the invite actually carries.
      return { module, granted_role: str(entry.granted_role) ?? '' };
    })
    .filter((offer): offer is OrgModuleGrantOffer => offer !== null);
}

/** One live invite, or `null` when there is no id to key it by. */
export function decodeOrgInvite(raw: unknown): OrgInviteRow | null {
  if (!isRecord(raw)) return null;
  const id = str(raw.id);
  if (id === null) return null;
  return {
    id,
    org_ref: str(raw.org_ref) ?? '',
    entity_class: str(raw.entity_class) ?? '',
    // An invite with no address names nobody. It still renders — the row is what a Global Admin
    // rescinds — so this degrades to '' and the panel says so rather than dropping the row and
    // leaving a live offer with no control anywhere.
    email: str(raw.email) ?? '',
    role: str(raw.role) ?? '',
    module_grants: decodeGrantOffers(raw.module_grants),
    issued_from: str(raw.issued_from) ?? '',
    issued_module: str(raw.issued_module),
    status: str(raw.status) ?? '',
    expires_at: str(raw.expires_at),
    created_at: str(raw.created_at),
    external: bool(raw.external),
  };
}

/** A page of live invites, dropping only the ones that cannot be keyed. */
export function decodeOrgInvites(body: unknown): OrgInviteRow[] {
  return extractList<unknown>(body)
    .map(decodeOrgInvite)
    .filter((row): row is OrgInviteRow => row !== null);
}

/**
 * The create operation's discriminated answer.
 *
 * ⚠ `already_admin` IS READ AS `=== true` LIKE EVERY OTHER BOOLEAN HERE, and the direction matters
 * in the usual way: an unreadable value resolves to `false`, which routes the caller to the ordinary
 * "we have invited them" reading. That is safe because it is paired with `invite` — a `false` flag
 * with no invite is the shape this decode calls unreadable, and the panel says nothing landed rather
 * than claiming either outcome.
 */
export function decodeOrgInviteCreated(body: unknown): OrgInviteCreated | null {
  if (!isRecord(body)) return null;
  const invite = decodeOrgInvite(body.invite);
  const alreadyAdmin = bool(body.already_admin);
  if (!alreadyAdmin && invite === null) return null;
  return { already_admin: alreadyAdmin, invite };
}

/**
 * An invite's offer (EP-MNT17), or `null` when the body names no invited address — the one field the
 * page cannot go without, since the account it makes is made at that address.
 */
export function decodeOrgInvitePreview(body: unknown): OrgInvitePreview | null {
  if (!isRecord(body)) return null;
  const email = str(body.email);
  if (email === null) return null;
  return {
    name: str(body.name),
    inviter_first_name: str(body.inviter_first_name),
    role: str(body.role) ?? '',
    modules: strings(body.modules),
    email,
  };
}

/** What the invitee learns on a successful redeem, or `null` when the body names no organization. */
export function decodeOrgInviteAccepted(body: unknown): OrgInviteAccepted | null {
  if (!isRecord(body)) return null;
  const orgRef = str(body.org_ref);
  if (orgRef === null) return null;
  return {
    org_ref: orgRef,
    entity_class: str(body.entity_class) ?? '',
    name: str(body.name),
    role: str(body.role) ?? '',
  };
}

/**
 * A page of connector rows.
 *
 * ⚠ ONLY THE SEVEN FIELDS THE WIRE CARRIES. Nothing here may synthesize an id-looking field: reading
 * this list is open to every admin of the organization, so a field this decode invented would be a
 * field every admin can see.
 */
export function decodeOrgConnectedAi(body: unknown): OrgConnectedAiRow[] {
  return extractList<unknown>(body)
    .map((raw): OrgConnectedAiRow | null => {
      if (!isRecord(raw)) return null;
      const id = str(raw.id);
      if (id === null) return null;
      return {
        id,
        // A suspended or removed partner degrades to a neutral label on the backend already; this is
        // the last guard, so a row still names something a person can act on.
        client_name: str(raw.client_name) ?? 'A connected AI',
        acting_admin_name: str(raw.acting_admin_name) ?? 'Someone',
        acting_admin_id: str(raw.acting_admin_id) ?? '',
        scopes: strings(raw.scopes),
        linked_at: str(raw.linked_at),
        last_active_at: str(raw.last_active_at),
      };
    })
    .filter((row): row is OrgConnectedAiRow => row !== null);
}

// ── Paging ───────────────────────────────────────────────────────────────────

/**
 * The next page's offset, or `undefined` when the list is exhausted.
 *
 * The whole rule, and it is forced by the unwrap: `meta` never arrives, so there is no total to
 * compare against. A page shorter than the page size is the last one, and the next offset is the
 * number of rows ALREADY LOADED — not a page index, which drifts the moment a page comes back short.
 */
export function nextOffset(
  lastPageLength: number,
  loadedSoFar: number,
  limit: number,
): number | undefined {
  return lastPageLength < limit ? undefined : loadedSoFar;
}

// ── Display helpers ──────────────────────────────────────────────────────────

/**
 * A roster row's display name.
 *
 * Guards the nulls so a missing surname never renders "First null", falls back to the email, and
 * then to a neutral word — a row must always name someone.
 */
export function orgMemberName(member: OrgConsoleMemberRow): string {
  const name = [member.first_name, member.last_name].filter(Boolean).join(' ').trim();
  if (name.length > 0) return name;
  if (member.email !== null && member.email.length > 0) return member.email;
  return 'A teammate';
}

/** The row's email, or '' when absent (the row then omits its secondary line). */
export function orgMemberEmail(member: OrgConsoleMemberRow): string {
  return member.email ?? '';
}

/** Whether a roster row is the signed-in admin. Compared on the backend's id, never on an email. */
export function isSelfMember(
  member: OrgConsoleMemberRow,
  selfUserId: string | null | undefined,
): boolean {
  return typeof selfUserId === 'string' && selfUserId.length > 0 && member.user_id === selfUserId;
}

/**
 * A one-line summary of a row's app access, for the roster's Apps column.
 *
 * Counts modules HELD and says so in words rather than rendering a chip per module: the roster
 * answers "who can act for this organization", and the app-by-app detail is one click away. A Global
 * Admin is never counted — their answer is "every app", a different sentence.
 */
export function moduleAccessSummary(member: OrgConsoleMemberRow): string {
  if (member.is_global_admin) return 'Every app';
  const held = member.modules.filter((row) => row.has_access).length;
  if (held === 0) return 'No apps';
  return held === 1 ? '1 app' : `${held} apps`;
}

/**
 * A one-line summary of what ONE invite offers, in this host's own words for a module.
 *
 * ⚠ A `Member` OFFER WITH NO MODULES SAYS SO. Under the invitation model nothing fans out behind an
 * empty list, so such an invite really does confer no apps — a real if useless state the server
 * accepts. Printing "No apps" is the honest reading; leaving the cell blank would look like a row
 * whose offer failed to load.
 *
 * A Global-Admin offer never carries modules and its answer is a different sentence entirely, so it
 * is returned before the list is consulted.
 */
export function inviteOfferSummary(
  invite: OrgInviteRow,
  moduleLabel: (slug: string) => string,
): string {
  if (invite.role.trim() === 'Owner') return 'Every app';
  const modules = invite.module_grants.map((offer) => moduleLabel(offer.module));
  if (modules.length === 0) return 'No apps';
  return modules.join(', ');
}

/**
 * Whether ONE invite carries a module — by an offered grant, or by having been issued from inside it.
 *
 * Either fact makes the offer that module's business: a module-issued invite always names the
 * module it was made from (and a legitimate one carries at most that module's grant), and an
 * org-issued one may include the module among others. A module's Team page narrows the invite list
 * on this predicate; the offer summary beside each row stays org-wide, so an invite that includes
 * the module still names every app it grants.
 */
export function inviteCarriesModule(invite: OrgInviteRow, module: string): boolean {
  const slug = module.trim();
  if (slug.length === 0) return false;
  return (
    invite.issued_module === slug || invite.module_grants.some((offer) => offer.module === slug)
  );
}

/**
 * How long a live invite has left, in whole days, or '' when it carries no readable expiry.
 *
 * ⚠ ROUNDS UP, and never below "Expires today". The server has already filtered expired rows out of
 * this list, so anything rendered here IS still redeemable — printing "Expired" or a negative count
 * for a row the server just called live would contradict the control sitting beside it. A clock skew
 * of a few seconds is the ordinary cause, and the honest reading of it is "today".
 */
export function inviteExpiresIn(invite: OrgInviteRow, now: number = Date.now()): string {
  if (invite.expires_at === null || invite.expires_at.length === 0) return '';
  const at = Date.parse(invite.expires_at);
  if (Number.isNaN(at)) return '';
  const days = Math.ceil((at - now) / 86_400_000);
  if (days <= 1) return 'Expires today';
  return `Expires in ${days} days`;
}

/** A short, locale-formatted timestamp, or '' when absent or unparseable. Never throws. */
export function formatOrgEventTime(iso: string | null): string {
  if (iso === null || iso.length === 0) return '';
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '';
  return new Date(ts).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * The DAY an instant fell on, as the viewer's own calendar reads it ("Oct 17, 2026") — a setting
 * card's last-change line (OM-17 seam A), where the time of day is noise. '' when unreadable.
 */
export function formatOrgChangeDay(iso: string | null): string {
  if (iso === null || iso.length === 0) return '';
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '';
  return new Date(ts).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Name a user id from the roster rows already in hand.
 *
 * ⚠ A MISS RENDERS "Someone", NOT THE ID. The event log's user columns are soft references — the
 * trail outlives the account — so an id with no matching row is the ordinary case for a departed
 * admin rather than an error, and printing the raw identifier would put a value on screen that
 * nobody can act on. `null` (an org-wide event with no subject) renders as '' so the cell stays empty.
 */
export function nameForOrgUserId(
  userId: string | null,
  members: readonly OrgConsoleMemberRow[],
): string {
  if (userId === null) return '';
  const match = members.find((member) => member.user_id === userId);
  if (match === undefined) return 'Someone';
  return orgMemberName(match);
}

// ── Org-account deactivation (OM-9) ─────────────────────────────────────────

/**
 * The deactivation act's answer. The act is DURABLE on a 200 whatever the body says, so an
 * unreadable body degrades to `null` — the done panel then prints no counts rather than guessing
 * them — and a missing count reads as zero.
 */
export function decodeOrgDeactivation(body: unknown): OrgDeactivationResult | null {
  if (!isRecord(body)) return null;
  return {
    deactivated_at: str(body.deactivated_at),
    memberships_revoked: count(body.memberships_revoked) ?? 0,
    sessions_revoked: count(body.sessions_revoked) ?? 0,
    session_failures: count(body.session_failures) ?? 0,
  };
}
